import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useDebounceValue } from '@/hooks/use-debounce';
import {
  FilterGroup,
  FirstInteractionFilterConfig,
  LastInteractionFilterConfig,
  LastInteractionTagFilterConfig,
  QueryOrdering,
  UsersInteractionsFilterConfig,
  UserConversationsCountFilterConfig,
  UserConversationLengthFilterConfig,
} from '@/api/generated/model';
import { buildFilterKindName, getVariable, sortFilterKindName, useGetFilterValues } from '@/comps/FilterPicker/utils';
import { FILTERS, isConversationCustomColumn, isCustomAggregate, isCustomColumn, isTag } from '@/models/common/filters';
import { formatDateConfigs, formatValueConfigs, isSpecialFilter } from '@/utils/filter-utils';
import { CheckedState } from '@radix-ui/react-checkbox';
import { List, ListItem } from '@/comps/list';
import { translate, translateSafe } from '@/utils/translations';
import { getFilterTooltip } from '@/utils/filter-tooltips';
import { AppliedFilterChip } from '@/comps/FilterPicker/AppliedFilterChip';
import { FilterKindValue } from '@/comps/FilterPicker/FilterKindValue';
import { FilterOperatorStep } from '@/comps/FilterPicker/FilterOperatorStep';
import { FilterValueStep } from '@/comps/FilterPicker/FilterValueStep';
import {
  buildGenericFilter,
  filterToOperator,
  FilterOperator,
  getOperatorLabel,
  isContainsFilter,
  supportsOperatorSelection,
} from '@/comps/FilterPicker/filter-operators';
import { WithLoadingContainer } from '@/comps/loading-spinner';
import { Chip } from '@/comps/chip';
import {
  BanIcon,
  ChevronRightIcon,
  Folders,
  GitCompare,
  LampDesk,
  Languages,
  Lightbulb,
  Loader2,
  MessageSquare,
  MessageSquarePlus,
  MessagesSquare,
  RectangleEllipsis,
  Sparkles,
  Star,
  Info,
  Tag,
  Telescope,
  TrashIcon,
  TriangleAlert,
  User2,
  UserCog2,
  Users2,
  UserX2,
  WholeWord,
  Workflow,
  HeartPulse,
} from 'lucide-react';
import { WhenForm } from '@/components/filters/filter-when-forms';
import { NumberOfInteractionsForm } from '@/components/filters/filter-number-of-interactions-form';
import { UserConversationsCountForm } from '@/components/filters/filter-user-conversations-count-form';
import { UserConversationLengthForm } from '@/components/filters/filter-user-conversation-length-form';
import { LastInteractionTagForm } from '@/components/filters/filter-last-interaction-tag-form';
import { Button } from '@/comps/primitives/button';
import { cn } from '@/utils/shad';
import { Tooltip } from '@radix-ui/react-tooltip';
import { TooltipContent, TooltipTrigger } from '@/comps/primitives/tooltip';
import { AvailableValue, FilterButtonProps, GenericFilter, GenericUserFilter } from '@/comps/FilterPicker/FilterPicker';
import { BehaviourIconZ, EmotionsIconZ, SentimentIconZ, UserFeedbackIconZ } from '@/constants/icons';
import { FilterPickerHeader } from '@/comps/FilterPicker/FilterPickerHeader';
import { useGlobalFiltersContext } from '@/contexts/filters/filter-context';
import { useAliasedKindName } from '@/contexts/alias';
import { humanizeString } from '@/utils/string.utils';
import { SearchInputV2 } from '../search-input';
import FilterSidebar, { FilterCategory, filterKindsByCategory } from './FilterSidebar';
import { useProjectsContext } from '@/providers/projects-provider';
import { useDialogActions, useDialogs } from '@/contexts/dialogs/hooks';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { QUERY_KEY_SAVED_FILTERS } from '@/constants/query-keys';
import { capitalize } from 'lodash';
import { DialogContent, DialogTitle, DialogDescription, DialogFooter } from '../primitives/dialog';
import { useLiveQuery, count } from '@tanstack/react-db';
import { favoriteFiltersCollection, filterUsageCollection } from './favorites';
import { useDeleteFilterGroup } from '@/api/generated/projects/projects';
export const filterTextValue = (filter: GenericUserFilter) => {
  if (filter.column === FILTERS.firstInteraction) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const castFilter = filter as any as FirstInteractionFilterConfig;
    return `users whose first interaction is ${formatDateConfigs(castFilter.values)}`;
  } else if (filter.column == FILTERS.lastInteraction) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const castFilter = filter as any as LastInteractionFilterConfig;
    return `users whose last interaction is ${formatDateConfigs(castFilter.values)}`;
  } else if (filter.column === FILTERS.usersInteraction) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const castFilter = filter as any as UsersInteractionsFilterConfig;
    return `users who have ${formatValueConfigs(castFilter.values.interactions_count)} ${
      castFilter.values.dates ? formatDateConfigs(castFilter.values.dates) : ''
    }`;
  } else if (filter.column === FILTERS.userConversationsCount) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const castFilter = filter as any as UserConversationsCountFilterConfig;
    return `users who have ${formatValueConfigs(castFilter.values.conversations_count)} conversations`;
  } else if (filter.column === FILTERS.userConversationLength) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const castFilter = filter as any as UserConversationLengthFilterConfig;
    return `users who have had conversations with ${formatValueConfigs(castFilter.values.interactions_count)} interactions`;
  } else if (filter.column === FILTERS.lastInteractionTag) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const castFilter = filter as any as LastInteractionTagFilterConfig;
    const search = castFilter.search?.trim();
    if (search && !castFilter.values?.length && !castFilter.select_all) {
      return `users whose last interaction tag "${castFilter.tag}" contains "${search}"`;
    }
    if (castFilter.select_all) {
      return `users whose last interaction tag "${castFilter.tag}" is All`;
    }
    const valuesText = castFilter.values?.length ? castFilter.values.join(', ') : '';
    return `users whose last interaction tag "${castFilter.tag}" is ${valuesText}`;
  }
  return '';
};

type SelectableOnlyProps = {
  open: boolean;
  setOpen: (v: boolean) => void;
  filterKind: string | null;
  setFilterKind: (v: string | null) => void;
  isPopover?: boolean;
  search: string;
  setSearch: (v: string) => void;
  type?: 'filter' | 'breakdown';
  ignoreBreakdowns?: string[];
  isGlobal?: boolean;
  filterSearch?: string;
  hideSavedFilters?: boolean;
  hideSaveButton?: boolean;
  hideFavoritesAndRecentlyUsedTabs?: boolean;
  hideSearch?: boolean;
  hideBackButton?: boolean;
  extraTabs?: FilterCategory[];
  defaultTab?: FilterCategory;
  timeFilterListPrepend?: ReactNode;
  favoritesListPrepend?: ReactNode;
  /** Explore group-by: show “Group is …” in the header (same row style as applied filters) */
  groupBySummaryColumn?: string | null;
};

export const FilterContent = <T extends GenericFilter>(props: FilterButtonProps<T> & SelectableOnlyProps) => {
  const {
    loading,
    filters,
    setFilters,
    filterKind,
    setFilterKind,
    timeRange,
    additionalFilters,
    volumeKind,
    open,
    setOpen,
    search: searchFilterValue,
    setSearch: setSearchFilterValue,
    filterSearch,
    isPopover,
    onChecked,
    showFilters,
    showFilterBy,
    availableFilterKinds,
    filterListPrepend,
    showDraftActions,
    draftActions,
    timeFilterListPrepend,
    favoritesListPrepend,
    showAdditionalFilterKinds,
    overrideSelectedFilters,
    ignoreFilters,
    ignoreBreakdowns,
    type,
    isGlobal = false,
    hideSavedFilters = false,
    hideSaveButton = false,
    hideFavoritesAndRecentlyUsedTabs = false,
    hideSearch = false,
    hideBackButton = false,
    extraTabs,
    defaultTab,
    className,
    groupBySummaryColumn,
  } = useMemo(() => {
    return { ...props, type: props.type ?? 'filter' };
  }, [props]);

  const { selectedProject, savedFilters } = useProjectsContext();
  const projectId = selectedProject?.id!;
  const mappedProjectId = selectedProject?.mappedProjectId;
  const { aggregateBy } = useGlobalFiltersContext();
  const dialogs = useDialogs();
  const aliasKindName = useAliasedKindName();

  const [filterSearchInternal, setFilterSearchInternal] = useState<string>('');
  const [activeTab, setActiveTab] = useState<FilterCategory>(defaultTab ?? 'all');
  const [draftOperator, setDraftOperator] = useState<FilterOperator | null>(null);
  const [containsDraft, setContainsDraft] = useState('');

  const filterSearchFinal = useMemo(() => {
    return filterSearch || filterSearchInternal;
  }, [filterSearch, filterSearchInternal]);

  const debouncedSearchFilterValue = useDebounceValue(searchFilterValue);

  const { data: allfavoriteFilters } = useLiveQuery((q) => q.from({ favorites: favoriteFiltersCollection }));

  const favoriteFilters = useMemo(() => {
    return allfavoriteFilters?.filter((f) => availableFilterKinds.includes(f.type));
  }, [allfavoriteFilters, availableFilterKinds]);

  const { data: allMostUsedFilters } = useLiveQuery((q) =>
    q
      .from({ usage: filterUsageCollection })
      .select((t) => ({ type: t.usage.type, count: count(t.usage) }))
      .groupBy((t) => t.usage.type)
      .orderBy((t) => count(t.usage), 'desc'),
  );

  const mostUsedFilters = useMemo(() => {
    return allMostUsedFilters?.filter((f) => availableFilterKinds.includes(f.type));
  }, [allMostUsedFilters, availableFilterKinds]);

  // Auto-select cohort filter when user_groups tab is active
  useEffect(() => {
    if (activeTab === 'user_groups' && !filterKind && availableFilterKinds.includes('cohort')) {
      setFilterKind('cohort');
    }
  }, [activeTab, filterKind, availableFilterKinds, setFilterKind]);

  const targetFilter = useMemo(() => {
    return filters.find((f) => f.column === filterKind);
  }, [filters, filterKind]);
  const targetFilterValues = useMemo(() => targetFilter?.values ?? [], [targetFilter?.values]);

  const [sortBy, setSortBy] = useState<QueryOrdering>({ by: getVariable(), direction: 'desc' });

  const filteredAvailableFilterKindsNoCategory = useMemo(() => {
    let filteredKinds = availableFilterKinds;

    // Filter out user filters when used for breakdown since they cannot be used as group by
    if (type === 'breakdown') {
      const userFilterTypes = [
        'first_interaction',
        'last_interaction',
        'users_interactions',
        'user_conversations_count',
        'user_conversation_length',
        'user_interaction_position',
        'last_interaction_tag',
      ];
      filteredKinds = filteredKinds.filter((kind) => !userFilterTypes.includes(kind));
    }

    return filteredKinds
      .filter((kind) => !additionalFilters?.find((f) => f.column === kind) || showAdditionalFilterKinds)
      .filter((kind) =>
        (aliasKindName(kind) ?? translate('filter', kind.split(':').length > 1 ? kind.split(':')[1] : kind))
          .toLowerCase()
          .includes(filterSearchFinal),
      );
  }, [availableFilterKinds, additionalFilters, showAdditionalFilterKinds, filterSearchFinal, type, aliasKindName]);

  const filteredAvailableFilterKinds = useMemo(() => {
    if (activeTab === 'saved_filters') {
      return savedFilters.map((filter) => filter.name);
    }
    if (activeTab === 'recently_used') {
      return mostUsedFilters.map((filter) => filter.type);
    }
    if (activeTab === 'favorites') {
      return favoriteFilters.map((filter) => filter.type);
    }
    if (type === 'breakdown' && ignoreBreakdowns !== undefined)
      return filterKindsByCategory(
        filteredAvailableFilterKindsNoCategory?.filter((item) => !ignoreBreakdowns.includes(item)),
        activeTab,
      );
    return filterKindsByCategory(filteredAvailableFilterKindsNoCategory, activeTab);
  }, [
    activeTab,
    type,
    ignoreBreakdowns,
    filteredAvailableFilterKindsNoCategory,
    savedFilters,
    mostUsedFilters,
    favoriteFilters,
  ]);

  const filtersMapByKind = useMemo(() => {
    return (overrideSelectedFilters ?? filters).reduce(
      (acc, filter) => {
        const valueEntries = isContainsFilter(filter)
          ? [filter.search?.trim() ?? '']
          : filter.column === FILTERS.lastInteractionTag || !Array.isArray(filter.values)
            ? [filterTextValue(filter)].filter(Boolean)
            : filter.values;

        if (acc[filter.column]) {
          acc[filter.column].push(...valueEntries);
        } else {
          acc[filter.column] = [...valueEntries];
        }
        return acc;
      },
      {} as Record<string, string[]>,
    );
  }, [overrideSelectedFilters, filters]);

  const valuesFetchEnabled =
    !!filterKind &&
    draftOperator !== 'contains'&&
    supportsOperatorSelection(filterKind) &&
    !isSpecialFilter(filterKind);

  const { availableFilterValues: tempAvailableFilterValues, isLoading } = useGetFilterValues<T>({
    filters,
    filters_v2: props.filters_v2,
    filterKind,
    timeRange,
    projectId: isGlobal ? (mappedProjectId ?? projectId) : projectId,
    search: debouncedSearchFilterValue,
    additionalFilters,
    filtersMapByKind,
    isSelectedAll: targetFilter?.select_all,
    volumeKind,
    sortBy,
    aggregateBy,
    enabled: valuesFetchEnabled,
  });

  // filter out values declared in ignoreFilters prop using its values
  // e.g.
  // (1)
  // filters = [{column:'test', values: ['foo', 'bar']}]
  // ignoreFilters = [{column: 'test', values: ['bar']}]
  // availableFilterValues = if same filterKind, exclude same values
  const availableFilterValues = useMemo(() => {
    if (!ignoreFilters) return tempAvailableFilterValues;
    const ignoreFilter = ignoreFilters.find((f) => f.column === filterKind);
    if (!ignoreFilter) return tempAvailableFilterValues;

    return tempAvailableFilterValues?.filter((av) => {
      // if same filterKind, exclude same values
      return !ignoreFilter.values.includes(av.value);
    });
  }, [ignoreFilters, filterKind, tempAvailableFilterValues]);

  const showSelectAll = (availableFilterValues?.length ?? 0) > 1;
  const filterKinds = filters.map((f) => f.column);

  const selectAllMap = useMemo(() => {
    const map: Record<string, boolean> = {};
    filterKinds.forEach((kind) => {
      const targetFilter = filters.find((f) => f.column === kind);
      map[kind] = !!targetFilter?.select_all;
    });
    return map;
  }, [filters, filterKinds]);

  const availableFilterValuesStrings = useMemo(
    () => availableFilterValues?.map((a) => a.value) ?? [],
    [availableFilterValues],
  );

  const draftIsExclude = draftOperator === 'is_not';

  const { handleOnCheckSelectAll, hasSelectedAll, allValuesSnapshot } = useFilterSelectAll<T>({
    filters,
    filterKind,
    availableFilterValues: availableFilterValuesStrings,
    setFilters,
    isExclude: draftIsExclude,
  });

  // ====================================================
  // FUNCTIONS
  // ====================================================

  const resetBuilderDraft = useCallback(() => {
    setDraftOperator(null);
    setContainsDraft('');
  }, []);

  const reset = () => {
    setFilterKind(null);
    resetBuilderDraft();
    setOpen(false);
    setSearchFilterValue('');
    setFilterSearchInternal('');
  };

  const handleBuilderBack = useCallback(() => {
    if (draftOperator) {
      setDraftOperator(null);
      setContainsDraft('');
      return;
    }
    setFilterKind(null);
    resetBuilderDraft();
  }, [draftOperator, setFilterKind, resetBuilderDraft]);

  const handleSelectField = useCallback(
    (kind: string) => {
      setFilterSearchInternal('');
      setFilterKind(kind);
      if (type === 'breakdown') {
        setOpen(!open);
        return;
      }

      resetBuilderDraft();
    },
    [open, resetBuilderDraft, setFilterKind, setOpen, type],
  );

  const handleSelectOperator = useCallback(
    (operator: FilterOperator) => {
      setDraftOperator(operator);
      if (operator === 'contains') {
        const existing = filters.find((f) => f.column === filterKind);
        setContainsDraft(existing?.search?.trim() ?? '');
        setSearchFilterValue('');
      }
    },
    [filterKind, filters, setSearchFilterValue],
  );

  const handleFilterChange = useCallback(
    (values: string[], checked: boolean, selectAllOverride?: boolean) => {
      const restFilters = filters.filter((f) => f.column !== filterKind);
      const operator = draftOperator ?? (targetFilter ? filterToOperator(targetFilter) : 'equals');

      if (values.length) {
        const newFilter = buildGenericFilter({
          column: filterKind!,
          operator,
          values,
          selectAll: selectAllOverride ?? targetFilter?.select_all,
        }) as T;
        restFilters.push(newFilter);
      }

      setFilters(restFilters);
      onChecked?.(checked, filterKind, restFilters);
    },
    [draftOperator, filterKind, filters, onChecked, setFilters, targetFilter],
  );

  const handleApplyContains = useCallback(() => {
    const text = containsDraft.trim();
    if (!filterKind || !text) return;

    const restFilters = filters.filter((f) => f.column !== filterKind);
    restFilters.push(
      buildGenericFilter({
        column: filterKind,
        operator: 'contains',
        values: [],
        containsText: text,
      }) as T,
    );
    setFilters(restFilters);
    onChecked?.(false, filterKind, restFilters);
    setSearchFilterValue('');
  }, [containsDraft, filterKind, filters, onChecked, setFilters, setSearchFilterValue]);

  const sortKindsWithFavoritesFirst = useCallback(
    (kinds: string[]) => {
      const favoriteTypes = new Set(favoriteFilters?.map((f) => f.type) ?? []);
      return [...kinds].sort((a, b) => {
        const aFav = favoriteTypes.has(a);
        const bFav = favoriteTypes.has(b);
        if (aFav !== bFav) return aFav ? -1 : 1;
        return sortFilterKindName(a, b, aliasKindName);
      });
    },
    [favoriteFilters, aliasKindName],
  );

  const showOperatorStep =
    open &&
    filterKind &&
    type === 'filter' &&
    !isSpecialFilter(filterKind) &&
    supportsOperatorSelection(filterKind) &&
    draftOperator === null;

  const showValueStep =
    open &&
    filterKind &&
    type === 'filter' &&
    !isSpecialFilter(filterKind) &&
    supportsOperatorSelection(filterKind) &&
    draftOperator !== null;

  function handleSelectFilterValue(value: AvailableValue, checked: CheckedState) {
    // When select_all is active we seed from allValuesSnapshot (the first 100 values returned by the
    // API at the time "Select All" was clicked) rather than the handful of preview values stored in
    // filter.values. This fixes the common case: unchecking one item correctly removes only that item.
    //
    // Known limitation: the interaction-aggregates-v2 endpoint is capped at 100 results (hardcoded in the FE). If the total
    // number of distinct values exceeds 100, any item beyond position 100 is absent from allValuesSnapshot.
    // Transitioning from select_all to an explicit list by unchecking will therefore silently drop those
    // out-of-range items. A proper fix requires backend support for excluded_values on top of select_all
    // (tracked in Linear).
    const baseValues = hasSelectedAll ? allValuesSnapshot : (targetFilterValues as string[]);
    const valuesSet = new Set(baseValues);

    if (checked) {
      valuesSet.add(value.value);
      if (filterKind) {
        filterUsageCollection.insert({ id: crypto.randomUUID(), type: filterKind, timestamp: Date.now() });
      }
    } else {
      valuesSet.delete(value.value);
    }

    // Editing values while select_all is active converts the filter to the
    // explicit list — otherwise select_all short-circuits the values on the
    // backend and the query keeps matching everything.
    handleFilterChange(Array.from(valuesSet), false, hasSelectedAll ? false : undefined);
  }

  const handleChangeSortByDirection = useCallback(
    (by: QueryOrdering['by']) => {
      const newDirection = sortBy.direction === 'asc' ? 'desc' : 'asc';
      setSortBy({ by, direction: newDirection });
    },
    [sortBy],
  );

  const handleChangeSortBy = (v: QueryOrdering['by']) => {
    const isAlpha = v === 'alphabetical';
    // NOTES: for alpha, "asc" is A-Z
    const dir = isAlpha ? 'asc' : 'desc';

    setSortBy({ by: v, direction: dir });
  };

  const showGroupBySummaryRow = type === 'breakdown' && Boolean(groupBySummaryColumn);
  /** Avoid stacking `gap-2` with outer `pt-2` so “Group by” has the same 8px above (root) and below (pb-2) before the divider. */
  const compactGroupByHeaderGap = showGroupBySummaryRow && !(showFilters && filterKinds.length > 0);

  // ====================================================
  // JSX
  // ====================================================
  return (
    <div className={cn('flex h-full w-full flex-col pt-2', className)}>
      <header
        className={cn(
          'sticky top-0 z-40 flex min-w-100 flex-col border-b bg-background text-[12px] shadow-sm',
          compactGroupByHeaderGap ? 'gap-0' : 'gap-2',
        )}
      >
        {/* ================== TITLE ================== */}
        {type === 'breakdown' && groupBySummaryColumn ? (
          <div className="flex flex-col">
            <FilterPickerHeader
              className="pt-0 pb-2"
              showFilters={showFilters}
              showFilterBy={showFilterBy}
              filterKinds={filterKinds}
              filterKind={filterKind}
              filters={filters}
              setFilterKind={setFilterKind}
              isPopover={isPopover}
              type={type}
              hideSaveButton={hideSaveButton}
              hideBackButton={hideBackButton}
              onBack={handleBuilderBack}
            />
            <List className={cn('z-50 flex flex-col gap-[5px] border-t px-3 py-2', hideSearch && 'mb-2')}>
              <FilterKindValue
                title="Group"
                text={buildFilterKindName(groupBySummaryColumn, aliasKindName)}
                readOnlyIs
                filterKind={groupBySummaryColumn as string | undefined}
                onClickClose={() => {}}
                showRemoveButton={false}
              />
            </List>
          </div>
        ) : (
          <FilterPickerHeader
            className={filterKinds.length > 0 && !hideBackButton ? 'pb-2' : undefined}
            showFilters={showFilters}
            showFilterBy={showFilterBy}
            filterKinds={filterKinds}
            filterKind={filterKind}
            filters={filters}
            setFilterKind={setFilterKind}
            isPopover={isPopover}
            type={type}
            hideSaveButton={hideSaveButton}
            hideBackButton={hideBackButton}
            onBack={handleBuilderBack}
          />
        )}
        {/* ================== SELECTED FILTER KIND VALUES ================== */}
        {showFilters && filterKinds.length > 0 && (
          <List className={cn('z-50 flex flex-col gap-[5px] px-3', hideSearch && 'mb-2')}>
            {filterKinds.map((kind, index) => {
              const filterValues = filtersMapByKind[kind]
                .map((value) =>
                  translate(kind as keyof typeof translate, value) === value
                    ? humanizeString(value)
                    : translate(kind as keyof typeof translate, value),
                )
                .join(', ');

              const targetFilter = filters.find((f) => f.column === kind);
              if (!targetFilter) return null;

              const operator = filterToOperator(targetFilter);

              return (
                filtersMapByKind[kind] && (
                  <AppliedFilterChip
                    key={kind}
                    filter={targetFilter}
                    fieldLabel={buildFilterKindName(kind, aliasKindName)}
                    operator={operator}
                    valueText={filterValues}
                    onRemove={() => setFilters(filters.filter((f, i) => f.column !== kind || i !== index))}
                    onClickField={() => {
                      setFilterKind(kind);
                      setDraftOperator(null);
                      setContainsDraft('');
                    }}
                    onClickOperator={() => {
                      setFilterKind(kind);
                      setDraftOperator(operator);
                      setContainsDraft(isContainsFilter(targetFilter) ? (targetFilter.search ?? '') : '');
                    }}
                    onClickValue={() => {
                      setFilterKind(kind);
                      setDraftOperator(operator);
                      setContainsDraft(isContainsFilter(targetFilter) ? (targetFilter.search ?? '') : '');
                      setSearchFilterValue('');
                    }}
                  />
                )
              );
            })}
          </List>
        )}
        {!hideSearch && (
          <div className="flex items-center gap-2 border-t px-2 py-2">
            <SearchInputV2
              placeholder={translateSafe('common', 'searchEllipsis')}
              search={filterSearchInternal}
              setSearch={(v) => {
                setFilterSearchInternal(v.toLowerCase());
              }}
            />
          </div>
        )}
      </header>

      <div className="relative flex min-h-0 w-full flex-1 items-start">
        <FilterSidebar
          availableFilterKinds={filteredAvailableFilterKindsNoCategory}
          setActiveTab={setActiveTab}
          activeTab={activeTab}
          filterKind={filterKind}
          setFilterKind={setFilterKind}
          hideSavedFilters={hideSavedFilters}
          hideFavoritesAndRecentlyUsedTabs={hideFavoritesAndRecentlyUsedTabs}
          extraTabs={extraTabs}
        />
        <div className="scrollbar h-full w-full flex-1 overflow-y-auto p-2">
          {/* ================== FILTER KINDS ================== */}
          {/* Time tab content – rendered only when the time tab is active */}
          {activeTab === 'time' && timeFilterListPrepend && (
            <List className="z-50 flex flex-col leading-4">{timeFilterListPrepend}</List>
          )}

          {((open && !filterKind && type === 'filter') || (open && type === 'breakdown')) &&
            activeTab !== 'saved_filters' &&
            activeTab !== 'time' && (
              <div>
                <WithLoadingContainer className="min-h-12.5" loading={!!loading} hideChildren>
                  <List className="z-50 flex flex-col leading-4">
                    {activeTab === 'favorites' ? favoritesListPrepend : filterListPrepend}
                    {sortKindsWithFavoritesFirst(filteredAvailableFilterKinds).map((kind) => {
                      const itemName = buildFilterKindName(kind, aliasKindName);
                      const isFavorite = favoriteFilters?.some((f) => f.type === kind);
                      const tooltipText = getFilterTooltip(kind);

                      return (
                        <ListItem
                          className="px-1 py-1 hover:bg-inherit"
                          key={kind}
                          onClick={() => handleSelectField(kind)}
                        >
                          <div className={cn('flex items-center justify-between leading-4')}>
                            <Chip
                              size="medium"
                              className="flex h-7 items-center rounded-base bg-background py-1.5 text-[12px] font-semibold"
                              variant="secondary"
                            >
                              <FilterIconV2 filterName={kind} />
                              <div className="flex items-center gap-1">
                                <span>{itemName}</span>
                                {tooltipText && (
                                  <Tooltip delayDuration={300}>
                                    <TooltipTrigger>
                                      <Info size={12} className="cursor-pointer text-gray-500 hover:text-white" />
                                    </TooltipTrigger>
                                    <TooltipContent>{tooltipText}</TooltipContent>
                                  </Tooltip>
                                )}
                              </div>
                            </Chip>

                            <div className="flex items-center gap-1">
                              {!!filtersMapByKind[kind] && showFilters && (
                                <Chip
                                  size="small"
                                  className="mr-1 ml-1 rounded-base bg-background text-[10px] leading-3 text-darkgrey"
                                  variant="secondary"
                                >
                                  {selectAllMap[kind] ? 'All' : filtersMapByKind[kind].length}{' '}
                                  {translateSafe('filter', 'applied')}
                                </Chip>
                              )}

                              <Star
                                className={cn(
                                  'hover:cursor-pointer',
                                  isFavorite ? 'fill-foreground text-foreground' : 'text-muted-foreground',
                                )}
                                size={16}
                                onClick={(evt) => {
                                  evt.stopPropagation();
                                  evt.preventDefault();

                                  if (isFavorite) {
                                    favoriteFiltersCollection.delete(kind);
                                  } else {
                                    favoriteFiltersCollection.insert({ type: kind });
                                  }
                                }}
                              />

                              <ChevronRightIcon className="text-muted-foreground" size={16} />
                            </div>
                          </div>
                        </ListItem>
                      );
                    })}
                  </List>
                </WithLoadingContainer>
              </div>
            )}

          {activeTab === 'saved_filters' && (
            <List className="z-50 flex flex-col leading-4">
              {savedFilters.map((filterGroup) => (
                <ListItem
                  key={filterGroup.id}
                  className="px-1 py-1 hover:bg-inherit"
                  onClick={() => {
                    setFilters(
                      filterGroup.filters.map((f) => ({
                        column: f.column,
                        values: f.values,
                        is_exclude: f.is_excluded,
                        search: (f as GenericFilter).search,
                      })) as T[], // unfortunate workaround because T is generic for some reason
                    );
                    setOpen(false);
                  }}
                >
                  <div className={cn('flex items-center justify-between leading-4')}>
                    <Tooltip>
                      <TooltipTrigger>
                        <Chip
                          size="medium"
                          className="flex h-7 items-center rounded-base bg-background py-1.5 text-[12px] font-semibold hover:cursor-help"
                          variant="secondary"
                        >
                          <Sparkles className="mr-1 inline" size={14} />
                          {filterGroup.name}
                        </Chip>
                      </TooltipTrigger>
                      <TooltipContent>
                        {filterGroup.filters.map((f) => (
                          <div key={f.column}>
                            <b>{aliasKindName(f.column) ?? humanizeString(f.column)}</b>{' '}
                            {getOperatorLabel(
                              (f as GenericFilter).search?.trim() && !f.values?.length
                                ? 'contains'
                                : f.is_excluded
                                  ? 'is_not'
                                  : 'equals',
                            )}{' '}
                            {(f as GenericFilter).search?.trim() && !f.values?.length
                              ? `"${(f as GenericFilter).search}"`
                              : f.values.map((v) => `"${capitalize(v)}"`).join(' or ')}
                          </div>
                        ))}
                      </TooltipContent>
                    </Tooltip>
                    <div className="flex items-center">
                      <Button
                        variant="outline"
                        size="iconSmall"
                        className="h-7 w-7"
                        onClick={(evt) => {
                          evt.stopPropagation();
                          evt.preventDefault();

                          dialogs.push({
                            content: <DeleteFilterGroupDialog filterGroup={filterGroup} projectId={projectId} />,
                          });
                        }}
                      >
                        <TrashIcon className="text-muted-foreground" size={16} />
                      </Button>

                      <ChevronRightIcon className="text-muted-foreground" size={16} />
                    </div>
                  </div>
                </ListItem>
              ))}
            </List>
          )}

          {/* ================== FILTER KIND > VALUES SECTION - FIRST INTERACTION ================== */}
          {open && filterKind && filterKind === FILTERS.firstInteraction && type === 'filter' && (
            <div className="z-0 bg-background p-2">
              <WhenForm
                title={'Users whose first interaction is'}
                timeRange={timeRange}
                onApply={(values) => {
                  setFilters([
                    ...(filters as any), //eslint-disable-line @typescript-eslint/no-explicit-any
                    {
                      column: filterKind,
                      values: values as any, //eslint-disable-line @typescript-eslint/no-explicit-any
                    },
                  ]);
                  setOpen(false);
                  setFilterKind(null);
                }}
              ></WhenForm>
            </div>
          )}

          {/* ================== FILTER KIND > VALUES SECTION - LAST INTERACTION ================== */}
          {open && filterKind && filterKind === FILTERS.lastInteraction && type === 'filter' && (
            <div className="z-0 bg-background p-2">
              <WhenForm
                title={'Users whose last interaction is'}
                timeRange={timeRange}
                onApply={(values) => {
                  setFilters([
                    //eslint-disable-next-line @typescript-eslint/no-explicit-any
                    ...(filters as any),
                    {
                      column: filterKind,
                      //eslint-disable-next-line @typescript-eslint/no-explicit-any
                      values: values as any,
                    },
                  ]);
                  setOpen(false);
                  setFilterKind(null);
                }}
              ></WhenForm>
            </div>
          )}

          {/* ================== FILTER KIND > VALUES SECTION - NUMBER OF INTERACTIONS ================== */}
          {open && filterKind && filterKind === FILTERS.usersInteraction && type === 'filter' && (
            <div className="z-0 bg-background p-2">
              <NumberOfInteractionsForm
                onApply={(values) => {
                  setFilters([
                    //eslint-disable-next-line @typescript-eslint/no-explicit-any
                    ...(filters as any),
                    {
                      column: filterKind,
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      values: values as any,
                    },
                  ]);
                  setOpen(false);
                  setFilterKind(null);
                }}
              ></NumberOfInteractionsForm>
            </div>
          )}

          {/* ================== FILTER KIND > VALUES SECTION - USER CONVERSATIONS COUNT ================== */}
          {open && filterKind && filterKind === FILTERS.userConversationsCount && type === 'filter' && (
            <div className="z-0 bg-background p-2">
              <UserConversationsCountForm
                onApply={(values) => {
                  setFilters([
                    //eslint-disable-next-line @typescript-eslint/no-explicit-any
                    ...(filters as any),
                    {
                      column: filterKind,
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      values: values as any,
                    },
                  ]);
                  setOpen(false);
                  setFilterKind(null);
                }}
              ></UserConversationsCountForm>
            </div>
          )}

          {/* ================== FILTER KIND > VALUES SECTION - USER CONVERSATION LENGTH ================== */}
          {open && filterKind && filterKind === FILTERS.userConversationLength && type === 'filter' && (
            <div className="z-0 bg-background p-2">
              <UserConversationLengthForm
                onApply={(values) => {
                  setFilters([
                    //eslint-disable-next-line @typescript-eslint/no-explicit-any
                    ...(filters as any),
                    {
                      column: filterKind,
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      values: values as any,
                    },
                  ]);
                  setOpen(false);
                  setFilterKind(null);
                }}
              ></UserConversationLengthForm>
            </div>
          )}

          {/* ================== FILTER KIND > VALUES SECTION - LAST INTERACTION TAG ================== */}
          {open && filterKind && filterKind === FILTERS.lastInteractionTag && type === 'filter' && (
            <div className="z-0 bg-background p-2">
              <LastInteractionTagForm
                timeRange={timeRange}
                initialValues={
                  targetFilter
                    ? {
                        tag: (targetFilter as unknown as LastInteractionTagFilterConfig).tag,
                        values: (targetFilter.values as string[]) ?? [],
                        is_exclude: targetFilter.is_exclude,
                        select_all: targetFilter.select_all,
                        search: (targetFilter as GenericFilter).search ?? undefined,
                      }
                    : undefined
                }
                onApply={(config) => {
                  const restFilters = filters.filter((f) => f.column !== filterKind);
                  setFilters([
                     
                    ...restFilters,
                    {
                      column: filterKind,
                      tag: config.tag,
                      values: config.values,
                      is_exclude: config.is_exclude,
                      select_all: config.select_all,
                      search: config.search,
                    } as any, // eslint-disable-line @typescript-eslint/no-explicit-any
                  ]);
                  setOpen(false);
                  setFilterKind(null);
                }}
              />
            </div>
          )}

          {/* ================== OPERATOR STEP ================== */}
          {showOperatorStep && (
            <div className="z-0 bg-background p-2">
              <p className="mb-2 px-1 text-[12px] font-semibold text-muted-foreground">
                {buildFilterKindName(filterKind, aliasKindName)}
              </p>
              <FilterOperatorStep onSelectOperator={handleSelectOperator} />
            </div>
          )}

          {/* ================== VALUE STEP ================== */}
          {showValueStep && draftOperator && (
            <FilterValueStep
              filterKind={filterKind}
              operator={draftOperator}
              containsText={containsDraft}
              onContainsTextChange={setContainsDraft}
              onApplyContains={handleApplyContains}
              searchFilterValue={searchFilterValue}
              setSearchFilterValue={setSearchFilterValue}
              sortBy={sortBy}
              onChangeSortBy={handleChangeSortBy}
              onChangeSortByDirection={handleChangeSortByDirection}
              availableFilterValues={availableFilterValues}
              isLoading={isLoading}
              targetFilterValues={targetFilterValues as string[]}
              hasSelectedAll={hasSelectedAll}
              showSelectAll={showSelectAll}
              onCheckSelectAll={handleOnCheckSelectAll}
              onSelectFilterValue={handleSelectFilterValue}
              volumeKind={volumeKind}
              onEmptyReset={reset}
            />
          )}
        </div>
      </div>
      {showDraftActions && draftActions && (
        <footer className="flex shrink-0 items-center justify-end gap-2 border-t bg-background px-3 py-2">
          <Button tooltip={{ text: 'Reset local changes' }} variant="outline" size="sm" onClick={draftActions.onReset}>
            Reset
          </Button>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex">
                <Button
                  variant="outline"
                  size="sm"
                  tooltip={{ text: 'Apply local changes for everyone' }}
                  disabled={!draftActions.canSaveForEveryone || draftActions.isSaving}
                  onClick={() => draftActions.onSaveForEveryone()}
                >
                  {draftActions.isSaving ? 'Saving...' : 'Save for everyone'}
                </Button>
              </span>
            </TooltipTrigger>
            {!draftActions.canSaveForEveryone && draftActions.saveDisabledMessage && (
              <TooltipContent>{draftActions.saveDisabledMessage}</TooltipContent>
            )}
          </Tooltip>
        </footer>
      )}
    </div>
  );
};

// ===================================================================
// MISC
// ===================================================================

export function FilterIconV2({ filterName }: { filterName: string }) {
  const Base = useMemo(() => {
    if (isTag(filterName)) return Tag;
    if (isCustomColumn(filterName)) return RectangleEllipsis;
    if (isConversationCustomColumn(filterName)) return RectangleEllipsis;
    if (isCustomAggregate(filterName)) return Folders;

    switch (filterName) {
      case 'cohort':
        return Users2;
      case 'keyword':
        return WholeWord;
      case 'data_source':
        return GitCompare;
      case 'topic':
      case 'manuals_topic':
      case 'conversation_topic':
      case 'conversation_manuals_topic':
        return LampDesk;
      case 'language':
        return Languages;
      case 'user':
        return User2;
      case 'pii':
        return UserX2;
      case 'user_intent':
      case 'conversation_intent':
        return Lightbulb;
      case 'user_action':
      case 'conversation_user_action':
      case 'manuals_user_action':
      case 'conversation_manuals_user_action':
        return Lightbulb;
      case 'interaction_label':
      case 'conversation_label':
        return Tag;
      case 'feedback_type':
      case 'user_feedback_v2':
        return UserFeedbackIconZ;
      case 'negative_intent':
      case 'negative_intent_v2':
        return BehaviourIconZ;
      case 'sentiment':
      case 'conversation_sentiment':
        return SentimentIconZ;
      case 'emotions':
        return EmotionsIconZ;
      case 'type_of_problem_mid':
      case 'type_of_problem_v2':
        return BanIcon;
      case 'user_rating':
        return Star;
      case 'has_user_comment':
        return MessageSquare;
      case 'type_of_risk':
      case 'negative_user_intent_v2':
        return TriangleAlert;
      case 'conversation_error_status':
        return HeartPulse;
      case 'positive_comment':
        return MessageSquarePlus;
      case 'conversation_session_length':
        return MessagesSquare;
      case 'out_of_scope_suggestion':
        return Telescope;
      case 'pii_processed':
        return UserCog2;
      default:
        return Workflow;
    }
  }, [filterName]);

  return <Base className="mr-1 inline" size={14} />;
}

type UseFilterSelectAllParams<T extends GenericFilter> = {
  filters: T[];
  filterKind: string | null;
  availableFilterValues: string[];
  setFilters: (filters: T[]) => void;
  isExclude?: boolean;
};

export function useFilterSelectAll<T extends GenericFilter>({
  filters,
  filterKind,
  availableFilterValues,
  setFilters,
  isExclude: isExcludeOverride,
}: UseFilterSelectAllParams<T>) {
  const hasSelectedAll = useMemo(() => {
    if (!filterKind) return false;

    const targetFilter = filters.find((f) => f.column === filterKind);
    if (!targetFilter) return false;

    return targetFilter.select_all ?? false;
  }, [filterKind, filters]);

  // Stores the full value list at the moment select_all becomes true.
  // filter.values is intentionally limited to 5 items for UI preview + backend payload,
  // so we keep a separate snapshot here to use as the seed when unchecking individual items.
  const [allValuesSnapshot, setAllValuesSnapshot] = useState<string[]>([]);

  // Reset snapshot when switching to a different filter kind.
  useEffect(() => {
    setAllValuesSnapshot([]);
  }, [filterKind]);

  // Populate snapshot whenever select_all is true and data is available.
  // The `allValuesSnapshot.length === 0` guard prevents overwriting a snapshot already
  // built by the user clicking "Select All".
  // This also handles select_all arriving from external state (e.g. saved filters).
  useEffect(() => {
    if (hasSelectedAll && availableFilterValues.length > 0) {
      setAllValuesSnapshot((prev) => (prev.length === 0 ? availableFilterValues : prev));
    }
    if (!hasSelectedAll) {
      setAllValuesSnapshot((prev) => (prev.length === 0 ? prev : []));
    }
  }, [hasSelectedAll, availableFilterValues]);

  const handleOnCheckSelectAll = useCallback(
    (checked: CheckedState) => {
      if (!filterKind) return;

      if (checked) {
        const restFilters = filters.filter((f) => f.column !== filterKind);

        const existingFilter = filters.find((f) => f.column === filterKind);
        const isExclude = isExcludeOverride ?? existingFilter?.is_exclude ?? false;

        const targetFilter = {
          column: filterKind,
          is_exclude: isExclude,
          select_all: checked !== 'indeterminate' ? checked : false,
          // When select all is true, it actually ignores the values but selects everything in the DB. So we slice the first 5 values so that something is visible in the filter rather than having 50+ filters in the UI.
          values: availableFilterValues?.slice(0, Math.min(availableFilterValues.length, 5)).map((av) => av) ?? [],
        } as T;
        setFilters([...restFilters, targetFilter]);
      } else {
        setFilters(filters.filter((f) => f.column !== filterKind));
      }
    },
    [availableFilterValues, filterKind, filters, isExcludeOverride, setFilters],
  );

  return { handleOnCheckSelectAll, hasSelectedAll, allValuesSnapshot };
}

export function DeleteFilterGroupDialog({ filterGroup, projectId }: { filterGroup: FilterGroup; projectId: string }) {
  const { close } = useDialogActions();
  const queryClient = useQueryClient();
  const { mutate: deleteFilterGroup, isLoading } = useDeleteFilterGroup({
    mutation: {
      onSuccess: () => {
        toast.success('Filter group deleted');
        queryClient.invalidateQueries({ queryKey: [QUERY_KEY_SAVED_FILTERS, projectId] });
        close();
      },
    },
  });

  return (
    <DialogContent className="flex flex-col gap-2">
      <DialogTitle>Delete saved filters</DialogTitle>
      <DialogDescription>
        Are you sure you want to delete <span className="font-bold">{filterGroup.name}</span>?
      </DialogDescription>
      <DialogFooter>
        <Button
          variant="destructive"
          onClick={() => {
            if (!filterGroup.id) {
              toast.error('Filter group id is missing');
              return;
            }

            deleteFilterGroup({ projectId, params: { filter_group_id: filterGroup.id! } });
          }}
          disabled={isLoading}
          PrependIcon={isLoading ? Loader2 : undefined}
          iconClassName="animate-spin"
        >
          Delete
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
